import { Geolocation } from '@capacitor/geolocation';
import { callFirebaseFunction } from 'boot/firebase';
import { get, isFunction, isObject } from 'lodash';
import { Platform } from 'quasar';
import { auth as useAuthStore } from 'src/store/auth';

export default {

  setGeolocationCallback(geolocationCallback) {
    this.geolocationCallback = geolocationCallback;
  }, // setGeolocationCallback()

  setIsRequestingGeolocation(isRequestingGeolocation) {
    this.isRequestingGeolocation = isRequestingGeolocation;
  }, // setIsRequestingGeolocation()

  setLatestGeolocation(geolocation) {
    if (!isObject(geolocation)) {
      return;
    }
    this.latestGeolocation = { ...geolocation, timestamp: new Date() };
  }, // setLatestGeolocation()

  async getGeolocation() {
    const processCoords = async (position) => {
      const { accuracy = null, longitude = null, latitude = null } = position.coords;

      // If we can't get the latitude or longitude, then geolocation is invalid, so we should mark all values as null.
      if (longitude === null || latitude === null) {
        return { accuracy: null, latitude: null, longitude: null, place: null };
      }

      let place = null;

      try {
        const placeData = await callFirebaseFunction(
          'getGeolocationPlace',
          { lat: latitude, lng: longitude },
        );
        place = get(placeData, 'data') || null;
      } catch (err) {
        console.error('Error getting geolocation place', err);
      }

      return { accuracy, latitude, longitude, place };
    };

    if (Platform.is.capacitor) {
      const position = await Geolocation.getCurrentPosition();

      return processCoords(position);
    }

    return new Promise((resolve, reject) => {
      if ('geolocation' in navigator) {
        navigator.geolocation.getCurrentPosition(
          (position) => resolve(processCoords(position)),
          (err) => reject(err),
          { timeout: 30000 },
        );
      } else {
        reject(new Error('Geolocation isn\'t available in browser'));
      }
    });
  }, // getGeolocation()

  executeGeolocationCallback(callback) {
    if (this.isRequestingGeolocation) {
      // If we are currently in process of requesting geolocation, then save the callback
      // for when the geolocation request is complete.
      this.setGeolocationCallback(callback);
      return true;
    }
    if (this.cachedRecentGeolocation !== null) {
      callback(this.cachedRecentGeolocation);
      this.setGeolocationCallback(null);
      return true;
    }
    // Return false if we can't currently obtain a valid geolocation
    // (e.g. if the user has disabled geolocation or if the latest geolocation is outdated)
    // This will allow the caller to prompt the user to obtain a new geolocation if necessary.
    callback(null, { message: 'Geolocation is not available' });
    this.setGeolocationCallback(null);
    return false;
  }, // executeGeolocationCallback()

  // Most devices require geolocation to be request on user interaction. Therefore, we need to
  // request geolocation on user interaction and then save it when we need it (regardless of user
  // interaction).
  // https://developer.mozilla.org/en-US/docs/Web/API/Geolocation/getCurrentPosition
  async refreshGeolocation(forceUpdate) {
    const auth = useAuthStore();

    if (auth.currentClient?.disableGeolocation) {
      return;
    }
    if (!forceUpdate
      && ((this.cachedRecentGeolocation !== null) || this.isRequestingGeolocation)) {
      return;
    }
    try {
      this.setIsRequestingGeolocation(true);
      const geolocation = await this.getGeolocation();
      this.setLatestGeolocation(geolocation);

      const callback = this.geolocationCallback;
      if (isFunction(callback)) {
        callback(geolocation);
        this.setGeolocationCallback(null);
      }
    } catch (err) {
      const callback = this.geolocationCallback;
      if (isFunction(callback)) {
        callback(null, { message: err.message });
        this.setGeolocationCallback(null);
      }
    } finally {
      this.setIsRequestingGeolocation(false);
    }
  }, // refreshGeolocation()

};
